import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CookieConsent from "@/components/CookieConsent";
import MilaChat from "@/components/MilaChat";
import DriverStoriesPopup from "@/components/DriverStoriesPopup";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Positions from "@/pages/Positions";
import PayBenefits from "@/pages/PayBenefits";
import FAQ from "@/pages/FAQ";
import Apply from "@/pages/Apply";
import Contact from "@/pages/Contact";
import Admin from "@/pages/Admin";
import Track from "@/pages/Track";
import CityJobPage from "@/pages/CityJobPage";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Fleet from "@/pages/Fleet";
import TermsOfService from "@/pages/TermsOfService";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CopyrightPolicy from "@/pages/CopyrightPolicy";
import Testimonials from "@/pages/Testimonials";
import HowItWorks from "@/pages/HowItWorks";
import Market from "@/pages/Market";
import MarketTerms from "@/pages/MarketTerms";
import MarketState from "@/pages/MarketState";
import MarketPricingDemo from "@/pages/MarketPricingDemo";
import DriverPublicProfile from "@/pages/DriverPublicProfile";
import Partners from "@/pages/Partners";
import RefundPolicy from "@/pages/RefundPolicy";
import AgreementPreviewPage from "@/pages/AgreementPreviewPage";
import Loyalty from "@/pages/Loyalty";
import ReferralRedirect from "@/pages/ReferralRedirect";
import LoyaltyBanner from "@/components/LoyaltyBanner";
import { ArrowRight } from "lucide-react";

/* Sticky mobile Apply CTA — hidden on /apply, /admin, /market */
function MobileApplyCTA() {
  const { pathname } = useLocation();
  if (pathname === "/apply" || pathname === "/admin" || pathname.startsWith("/blog/") || pathname.startsWith("/market")) return null;
  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden border-t border-[#0A0A0A]/10 bg-white/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
      <p className="text-xs text-foreground/60 leading-tight">
        <span className="font-semibold text-[#0A0A0A]">Now Hiring</span> CDL-A Drivers
      </p>
      <Link
        to="/apply"
        data-testid="mobile-sticky-apply"
        className="shrink-0 inline-flex items-center gap-2 font-head text-xs uppercase tracking-[0.2em] bg-primary text-black px-5 py-3 hover:bg-[#0A0A0A] hover:text-primary transition-colors duration-300"
      >
        Apply Now <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  );
}

function AppInner() {
  const { pathname } = useLocation();
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/positions" element={<Positions />} />
          <Route path="/pay-benefits" element={<PayBenefits />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/apply" element={<Apply />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/track" element={<Track />} />
          <Route path="/jobs/:citySlug" element={<CityJobPage />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:postSlug" element={<BlogPost />} />
          <Route path="/fleet" element={<Fleet />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/copyright" element={<CopyrightPolicy />} />
          <Route path="/partners" element={<Partners />} />
          <Route path="/loyalty" element={<Loyalty />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/market" element={<Market />} />
          <Route path="/market/terms" element={<MarketTerms />} />
          <Route path="/market/pricing" element={<MarketPricingDemo />} />
          <Route path="/market/:stateSlug" element={<MarketState />} />
          <Route path="/driver/:slug" element={<DriverPublicProfile />} />
          <Route path="/referral/:slug" element={<ReferralRedirect />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/agreement-preview/:type" element={<AgreementPreviewPage />} />
        </Routes>
      </main>
      <MobileApplyCTA />
      <Footer />
      <CookieConsent />
      {!pathname.startsWith("/admin") && !pathname.startsWith("/market") && <MilaChat />}
      <DriverStoriesPopup />
      {/* Loyalty Program promotional banner — shown once per session on non-market pages */}
      {!pathname.startsWith("/market") && !pathname.startsWith("/admin") && !pathname.startsWith("/loyalty") && <LoyaltyBanner />}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AppInner />
        </BrowserRouter>
        <Toaster
          position="top-center"
          theme="light"
          toastOptions={{
            style: {
              background: "#ffffff",
              color: "#0A0A0A",
              border: "2px solid #0A0A0A",
              fontWeight: "600",
            },
          }}
        />
      </div>
    </HelmetProvider>
  );
}

export default App;
